<template>
  <div class="min-h-[456px]">
    <h1
      v-if="pricing.length === 0 && !loading"
      class="font-title-6 mx-auto p-6 text-center opacity-40"
    >
      We are currently unable to load pricing. Please refresh the page.
    </h1>
    <div
      class="mx-auto flex min-h-0.5 w-full flex-col items-center justify-center gap-4 md:max-w-96 lg:max-w-full lg:flex-row lg:items-stretch lg:px-4 xl:px-0"
    >
      <template v-if="!loading && pricing.length > 0">
        <!-- Enterprise Plan - Using String for features -->
        <transition-group name="stagger" appear>
          <div
            v-for="(price, i) in pricing"
            :key="price.id"
            :style="{ transitionDelay: `${i * 150}ms` }"
            class="h-full w-full lg:flex-1"
          >
            <PricingCard
              :type="price.type"
              :title="price.name"
              :description="price.description"
              :features="price.features"
              :amount="price.amount"
              :interval="price.interval"
              :price-subtitle="price.priceSubtitle"
              :show-action="showAction"
              :button-loading="buttonActionLoading(price)"
              :button-disabled="buttonActionDisabled(price)"
              :button-text="buttonActionText(price)"
              :is-current="currentPriceId === price.id"
              @selected="handleClick(price)"
            />
          </div>
        </transition-group>
      </template>
      <template v-else-if="loading">
        <div v-for="i in 3" :key="i" class="h-full w-full lg:flex-1">
          <div
            :class="loadingCardMinHeight"
            class="flex h-full w-full min-w-[340px] flex-col rounded-xl border bg-white p-6 opacity-40 lg:p-12"
          >
            <div class="flex h-full flex-col">
              <Skeleton shape="circle" size="2.25rem" class="mb-6" />
              <Skeleton width="70%" height="1.75rem" class="mb-4" />
              <Skeleton width="100%" class="mb-2" />
              <Skeleton width="85%" class="mb-8" />

              <Skeleton width="45%" height="2.2rem" class="mb-3" />
              <Skeleton width="35%" class="mb-6" />

              <div class="my-2 h-px w-full bg-[#E6E6EA]" />

              <div class="mb-8 space-y-3">
                <Skeleton width="100%" />
                <Skeleton width="95%" />
                <Skeleton width="90%" />
              </div>

              <div class="mt-auto w-full">
                <Skeleton
                  width="100%"
                  height="2.75rem"
                  border-radius="0.5rem"
                />
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { MappedPrice } from '~/types/pricing/Price';
import { PriceProductKey } from '~/utils/enums/Price';
import PricingCard from './PricingCard.vue';

const prop = defineProps<{
  pricing: MappedPrice[];
  loading: boolean;
  showAction?: boolean;
  currentPriceId?: string | null;
  processingPriceId?: string | null;
}>();

const emit = defineEmits<{
  (e: 'selected', price: MappedPrice): void;
}>();

function buttonActionText(price: MappedPrice): string {
  if (price.type === PriceProductKey.ENTERPRISE) {
    return 'Contact Us';
  }

  if (prop.currentPriceId) {
    return prop.currentPriceId === price.id ? 'Current Plan' : 'Choose';
  }

  return 'Choose';
}

function buttonActionDisabled(price: MappedPrice): boolean {
  if (prop.processingPriceId) {
    return true;
  }

  if (prop.currentPriceId) {
    return prop.currentPriceId === price.id;
  }
  return false;
}

function buttonActionLoading(price: MappedPrice): boolean {
  if (prop.processingPriceId) {
    return prop.processingPriceId === price.id;
  }
  return false;
}

function handleClick(price: MappedPrice): void {
  emit('selected', price);
}

const loadingCardMinHeight = computed(() => {
  return prop.showAction ? 'min-h-[552px]' : 'min-h-[456px]';
});
</script>

<style scoped>
.stagger-enter-active {
  transition:
    opacity 0.5s ease,
    transform 0.5s ease;
}
.stagger-enter-from {
  opacity: 0;
  transform: translateY(20px);
}
</style>
